import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight, MessageSquare, Phone, Laptop, Download, Lock } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { testimonials } from '../data/testimonials';
import { siteConfig } from '../config/siteConfig';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const communications = [
  { title: "Parent-Teacher Meetings", desc: "Regular interactive meetings to discuss child development, milestones, and academic growth." },
  { title: "Detailed Progress Reports", desc: "Periodic qualitative progress reports highlighting sensory, motor, social, and early cognitive skills." },
  { title: "Active Event Updates", desc: "Instant updates on upcoming camps, festival celebrations, orientations, and school tours." },
  { title: "Child-Development Guidance", desc: "Direct counseling with teachers regarding child behavior, nutrition, and home discipline support." },
  { title: "WhatsApp Alert Groups", desc: "Classroom-specific broadcast alerts for daily activities, immediate notices, and emergency alerts." },
  { title: "Centralized School App", desc: "Configured portal for digital notices, fee updates, timetables, and teacher messages." }
];

const resources = [
  { title: "Preparing for Preschool", desc: "Tips to help children manage early separation anxiety, adjust to morning routines, and build classroom excitement." },
  { title: "Establishing Healthy Habits", desc: "Guidance on sleep schedules, morning routines, and healthy lunchbox recipes for preschoolers." },
  { title: "Building Communication Skills", desc: "How to encourage talking, storytelling, and early vocabulary building through everyday conversations at home." },
  { title: "Screen-Time Balance", desc: "Healthy alternatives to tablets and television, including sensory play, reading, and board games." }
];

export default function ParentsCorner() {
  const [current, setCurrent] = useState(0);
  const featured = testimonials.slice(0, 3);
  const reviewBoard = testimonials.slice(3);

  const prev = () => setCurrent((c) => (c - 1 + featured.length) % featured.length);
  const next = () => setCurrent((c) => (c + 1) % featured.length);

  return (
    <div>
      <PageHero 
        title="School and Parents as Partners" 
        subtitle="We believe children grow best when parents and educators communicate regularly, understand one another, and work together with care and consistency." 
        breadcrumb="Parents Corner" 
      />

      {/* Parent Communication */}
      <section id="communication" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Collaboration</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Parent Communication</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">We maintain transparent, structured, and regular touchpoints with families to support holistic development.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {communications.map((comm, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="cartoon-card bg-white rounded-3xl p-8 border border-gray-100 shadow-sm"
              >
                <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-3">{comm.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{comm.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Parent Testimonials Carousel */}
      <section id="testimonials" className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Success Stories</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Parent Testimonials</h2>
          </motion.div>

          <div className="relative bg-white rounded-3rem p-8 md:p-12 shadow-lg border border-gray-100">
            <Quote size={48} className="text-[#FDE8E9] mb-4" fill="#FDE8E9" />
            <p className="text-gray-700 text-lg md:text-xl leading-relaxed italic mb-8">"{featured[current].text}"</p>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <img src={featured[current].avatar} alt={featured[current].name} className="w-14 h-14 rounded-full border-3 border-[#FDE8E9] object-cover" />
                <div>
                  <div className="font-nunito font-bold text-lg text-[#1A1A1A]">{featured[current].name}</div>
                  <div className="text-gray-500 text-sm">{featured[current].role}</div>
                  <div className="flex gap-0.5 mt-1">{[...Array(featured[current].rating)].map((_, j) => <Star key={j} size={14} fill="#FBBF24" className="text-yellow-400 border-none" />)}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={prev} className="w-11 h-11 bg-[#FDE8E9] hover:bg-[#C8161D] text-[#C8161D] hover:text-white rounded-full flex items-center justify-center transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={next} className="w-11 h-11 bg-[#C8161D] hover:bg-[#A61217] text-white rounded-full flex items-center justify-center transition-colors">
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Wave fill="white" bg="#F8FAFC" />

      {/* Review Board & Google Reviews */}
      <section id="reviews" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Feedback</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Parent Review Board</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {reviewBoard.map((item, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-3rem p-8 border border-gray-100 flex flex-col justify-between">
                <div>
                  <div className="flex gap-0.5 mb-4">{[...Array(item.rating)].map((_, j) => <Star key={j} size={14} fill="#FBBF24" className="text-yellow-400" />)}</div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">"{item.text}"</p>
                </div>
                <div className="flex items-center gap-3">
                  <img src={item.avatar} alt={item.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="font-nunito font-bold text-[#1A1A1A] text-sm">{item.name}</div>
                    <div className="text-gray-400 text-xs">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Google Reviews Card */}
          <div className="bg-[#FEF9C3] rounded-3rem p-8 md:p-12 border border-[#ca8a04]/20 max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Star size={24} fill="#FBBF24" className="text-yellow-500" />
                <span className="font-nunito font-extrabold text-2xl text-[#1A1A1A]">4.9 / 5 Rating</span>
              </div>
              <h3 className="font-nunito font-extrabold text-2xl text-[#1A1A1A] mb-3">Google Business Reviews</h3>
              <p className="text-gray-600 text-sm leading-relaxed max-w-lg">
                We are proud of the love and trust parents share. Visit our Google profile to read more reviews or share your own experience with us!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <a href={siteConfig.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary text-center justify-center">
                Read Google Reviews
              </a>
              <a href={siteConfig.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="btn-outline text-center justify-center">
                Share Your Experience
              </a>
            </div>
          </div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* App & Fee Payment */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* School Mobile App */}
            <motion.div 
              id="mobile-app"
              className="bg-white rounded-3rem p-10 shadow-lg border border-gray-100 flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <span className="section-label mb-4">Mobile App</span>
                <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-4">School Mobile App</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Access notices, events, timetables, photo galleries, attendance tracker, and teacher communication direct from your mobile device.
                </p>
                <h4 className="font-nunito font-bold text-[#1A1A1A] mb-3">App Access Features:</h4>
                <ul className="space-y-2 text-sm text-gray-500 mb-8 font-poppins">
                  <li className="flex items-center gap-2">✓ Dynamic school notification board</li>
                  <li className="flex items-center gap-2">✓ Timetables &amp; event updates</li>
                  <li className="flex items-center gap-2">✓ Child attendance &amp; pickup alerts</li>
                  <li className="flex items-center gap-2">✓ Secure teacher message inbox</li>
                </ul>
              </div>
              <div>
                {siteConfig.appLinks.playStore ? (
                  <div className="flex gap-4">
                    <a href={siteConfig.appLinks.playStore} target="_blank" rel="noopener noreferrer" className="btn-primary">Play Store</a>
                    <a href={siteConfig.appLinks.appStore} target="_blank" rel="noopener noreferrer" className="btn-outline">App Store</a>
                  </div>
                ) : (
                  <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-gray-100 flex items-center gap-3">
                    <Laptop size={20} className="text-[#C8161D]" />
                    <p className="text-xs text-gray-500">
                      <strong>Parent App:</strong> Contact the administration office to receive your credentials and setup instructions.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Online Fee Payment */}
            <motion.div 
              id="fee-payment"
              className="bg-white rounded-3rem p-10 shadow-lg border border-gray-100 flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div>
                <span className="section-label mb-4">Fees</span>
                <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-4">Secure Fee Payment</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Pay registration, monthly, or transport fees online using our secure external payment gateway portal (Razorpay).
                </p>
                <div className="bg-[#FDE8E9] rounded-2xl p-5 border border-[#C8161D]/15 flex items-start gap-3 mb-8">
                  <Lock size={20} className="text-[#C8161D] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <strong>Payment Safety:</strong> You will be redirected to a secure merchant page hosted by Razorpay. Do not share credentials or OTP details with anyone.
                  </p>
                </div>
              </div>
              <div>
                <a href={siteConfig.paymentUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full justify-center">
                  Go to Secure Payment Portal
                </a>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <Wave fill="white" bg="#F8FAFC" />

      {/* Parent Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Guides</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Parent Resources</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Downloadable tip sheets and checklists to help support your child's home routines.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {resources.map((res, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-3rem p-8 border border-gray-100 flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-2">{res.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{res.desc}</p>
                  <a href="#blog" className="text-[#C8161D] font-bold text-xs hover:underline flex items-center gap-1">
                    Read Related Article →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
